import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { Routes } from '~/domains/common/constants/routes';
import { useAppDownloadPrompt, PromptLocation } from '~/hooks/useAppDownloadPrompt';

type UseDepositContinueHandlerParams = {
  redirectTo?: string;
  contestId?: string;
  onClose?: VoidFunction;
};

/**
 * Shared hook for handling continue button logic in deposit success flows.
 * Handles app download prompt, redirectTo, contestId, and fallback navigation.
 *
 * @param params - Parameters for continue handler
 * @returns Continue handler function
 */
export function useDepositContinueHandler({
  redirectTo,
  contestId,
  onClose,
}: UseDepositContinueHandlerParams) {
  const router = useRouter();
  const handleShowAppDownloadPrompt = useAppDownloadPrompt(PromptLocation.DEPOSIT);

  const navigateWithModalClose = useCallback(
    (url: string, skipIfSamePage = false) => {
      if (skipIfSamePage) {
        const isSamePage = router.asPath === url || router.pathname === url;
        if (isSamePage) {
          // Just close modal, don't navigate
          if (onClose) {
            onClose();
          }
          return;
        }
      }

      if (onClose) {
        // Navigate first, then close modal
        // This ensures navigation happens even if component unmounts when modal closes
        router.push(url);
        // Close modal after navigation starts
        // Use setTimeout to ensure navigation is initiated before modal closes
        setTimeout(() => {
          onClose();
        }, 0);
      } else {
        // No modal to close, navigate directly
        router.push(url);
      }
    },
    [router, onClose]
  );

  const handleContinue = useCallback(() => {
    // Step 1: Call app download prompt first
    // If it redirects (returns truthy), stop execution
    if (handleShowAppDownloadPrompt({ redirectTo })) {
      return;
    }

    // Step 2: If redirectTo exists, redirect to that URL
    if (redirectTo) {
      navigateWithModalClose(redirectTo, true);
      return;
    }

    // Step 3: If contestId exists, redirect to contest detail
    if (contestId) {
      navigateWithModalClose(Routes.contestDetail(contestId));
      return;
    }

    // Step 4: Otherwise, redirect to contest lobby
    navigateWithModalClose(Routes.contestLobby());
  }, [handleShowAppDownloadPrompt, redirectTo, contestId, navigateWithModalClose]);

  return handleContinue;
}
